<script>
import { authMethods, notificationMethods } from "@/state/helpers";
import appConfig from "@/app.config";
import { VueTelInput } from "vue-tel-input";
import "vue-tel-input/dist/vue-tel-input.css";
import { required, email, minLength, sameAs } from "vuelidate/lib/validators";
/**
 * Register component
 */
export default {
  page: {
    title: "Register",
    meta: [
      {
        name: "description",
        content: appConfig.description,
      },
    ],
  },
  components: { VueTelInput },
  data() {
    return {
      loader: false,
      user: {
        first_name: "",
        last_name: "",
        phone: "",
        email: "",
        password: "",
        confirm_password: "",
      },
      submitted: false,
      showPassword: false,
      showConfirmPassword: false,
      regError: "",
      tryingToRegister: false,
      isRegisterError: false,
      registerSuccess: false,
      terms: false,
      privacy: false,
      rules: {
        terms: [(v) => !!v || "Accepting Terms of Service is required."],
        privacy: [(v) => !!v || "Accepting privacy policy is required."],
      },
    };
  },
  validations: {
    user: {
      first_name: {
        required,
      },
      last_name: {
        required,
      },
      email: {
        required,
        email,
      },
      phone: {
        required,
      },
      password: {
        required,
        minLength: minLength(3),
      },
      confirm_password: {
        required,
        sameAsPassword: sameAs("password"),
      },
    },
    terms: {
      required,
    },
    privacy: {
      required,
    },
  },
  computed: {
    notification() {
      return this.$store ? this.$store.state.notification : null;
    },
  },
  methods: {
    ...authMethods,
    ...notificationMethods,
    // Try to register the user in with the email, username
    // and password they provided.
    signup() {
      // stop here if form is invalid
      this.regError = "";
      this.submitted = true;
      this.$v.$touch();
      this.validate();

      if (!this.$v.$invalid && this.validate()) {
        this.loader = true;
        this.tryingToRegister = true;
        this.register(this.user)
          .then(() => {
            this.reset();
            this.resetValidation();
            this.submitted = false;
            this.regError = "";
            this.tryingToRegister = false;
            this.isRegisterError = false;
            this.$router.push("/dashboard");
          })
          .catch((err) => {
            this.submitted = false;
            this.tryingToRegister = false;
            this.regError = "Error! try again later";
            if ("response" in err) {
              if (err.response.status === 422) {
                this.regError = "";
                const errors = err.response.data;
                Object.values(errors).forEach((validations) => {
                  validations.forEach((validation) => {
                    this.regError += `${validation}<br>`;
                  });
                });
              } else if (
                err.response.status !== 500 ||
                ("data" in err.response.data &&
                  "message" in err.response.data.data)
              ) {
                this.regError = err.response.data.data.message;
              }
            }
            this.isRegisterError = true;
          })
          .finally(() => {
            this.loader = false;
          });
      }
    },
    validate() {
      return this.$refs.form.validate();
    },
    reset() {
      this.$refs.form.reset();
    },
    resetValidation() {
      this.$refs.form.resetValidation();
    },
  },
};
</script>

<template>
  <div class="fill-height">
    <v-container fluid id="registration" class="m-0 p-0">
      <v-row class="px-0 d-flex flex-row fill-height">
        <v-col class="navy d-none d-md-flex" md="6">
          <div class="logo d-flex">
            <img src="@/assets/images/logo-light.png" alt="WeStore" />
          </div>
          <v-row
            class="row-content d-flex flex-column justify-start align-center"
          >
            <v-col class="d-flex" cols="12" md="10">
              <h1 class="text-h2 font-weight-bold pink--text">
                Sign Up & Get a Quote
              </h1>
            </v-col>
            <v-col cols="12" md="10">
              <p class="text-h3 font-weight-bold white--text">
                Solving the hassles of your storage and delivery.
              </p>
            </v-col>
          </v-row>
        </v-col>
        <v-col class="d-flex">
          <div class="logo d-flex d-md-none">
            <a href="/">
              <img src="@/assets/images/logo-dark.png" alt="WeStore" />
            </a>
          </div>
          <div class="home d-flex">
            <a href="/">
              <v-icon x-large> mdi-home-outline </v-icon>
            </a>
          </div>
          <v-row
            class="form-content d-flex flex-column justify-start align-center"
          >
            <v-col class="col-xs-12 col-md-10">
              <v-card flat>
                <v-container>
                  <v-form ref="form" @submit.prevent="signup">
                    <v-row>
                      <v-col cols="12" md="6">
                        <b-form-group
                          class="mb-3"
                          label-size="lg"
                          :label="$t('First Name *')"
                        >
                          <b-form-input
                            v-model="user.first_name"
                            type="text"
                            :class="{
                              'is-invalid':
                                submitted && $v.user.first_name.$error,
                            }"
                          ></b-form-input>
                          <div
                            v-if="submitted && $v.user.first_name.$error"
                            class="invalid-feedback"
                          >
                            field is required
                          </div>
                        </b-form-group>
                      </v-col>
                      <v-col cols="12" md="6">
                        <b-form-group
                          class="mb-3"
                          label-size="lg"
                          :label="$t('Last Name *')"
                        >
                          <b-form-input
                            v-model="user.last_name"
                            type="text"
                            :class="{
                              'is-invalid':
                                submitted && $v.user.last_name.$error,
                            }"
                          ></b-form-input>
                          <div
                            v-if="submitted && $v.user.last_name.$error"
                            class="invalid-feedback"
                          >
                            field is required
                          </div>
                        </b-form-group>
                      </v-col>
                    </v-row>
                    <b-form-group
                      class="mb-3"
                      label-size="lg"
                      :label="$t('Recipient Phone *')"
                    >
                      <VueTelInput
                        v-model="user.phone"
                        :defaultCountry="'AE'"
                        :preferred-countries="['AE', 'SA', 'EG', 'US']"
                        :dropdownOptions="{
                          showDialCodeInSelection: true,
                          showDialCodeInList: true,
                          showFlags: true,
                          showSearchBox: true,
                        }"
                        :inputOptions="{
                          placeholder: 'Phone',
                          maxlength: 15,
                          required: true,
                        }"
                        :invalidMsg="'invalid number'"
                        :validCharactersOnly="true"
                        :mode="'international'"
                        :styleClasses="
                          submitted && $v.user.phone.$error
                            ? 'border border-1 border-danger '
                            : 'tel-border'
                        "
                        :class="{
                          'is-invalid': submitted && $v.user.phone.$error,
                        }"
                      />
                      <div
                        v-if="submitted && $v.user.phone.$error"
                        class="invalid-feedback"
                      >
                        field is required
                      </div>
                    </b-form-group>

                    <b-form-group
                      class="mb-3"
                      label-size="lg"
                      :label="$t('Email *')"
                    >
                      <b-form-input
                        v-model="user.email"
                        type="text"
                        :class="{
                          'is-invalid': submitted && $v.user.email.$error,
                        }"
                      ></b-form-input>
                      <div
                        v-if="submitted && !$v.user.email.required"
                        class="invalid-feedback"
                      >
                        field is required
                      </div>
                      <div
                        v-if="submitted && !$v.user.email.email"
                        class="invalid-feedback"
                      >
                        Enter a valid email
                      </div>
                    </b-form-group>

                    <b-form-group
                      class="mb-3"
                      label-size="lg"
                      :label="$t('Password *')"
                    >
                      <div class="position-relative">
                        <b-form-input
                          v-model="user.password"
                          :type="showPassword ? 'text' : 'password'"
                          :class="{
                            'is-invalid': submitted && $v.user.password.$error,
                          }"
                        ></b-form-input>
                        <v-icon
                          class="position-absolute"
                          style="right: 5px; top: 8px"
                          @click="showPassword = !showPassword"
                          >{{
                            showPassword ? "mdi-eye" : "mdi-eye-off"
                          }}</v-icon
                        >
                        <div
                          v-if="submitted && !$v.user.password.required"
                          class="invalid-feedback"
                        >
                          field is required
                        </div>
                        <div
                          v-if="submitted && !$v.user.password.minLength"
                          class="invalid-feedback"
                        >
                          Password must be least 6 characters.
                        </div>
                      </div>
                    </b-form-group>

                    <b-form-group
                      class="mb-3"
                      label-size="lg"
                      :label="$t('Confirm Password *')"
                    >
                      <div class="position-relative">
                        <b-form-input
                          v-model="user.confirm_password"
                          :type="showConfirmPassword ? 'text' : 'password'"
                          :class="{
                            'is-invalid':
                              submitted && $v.user.confirm_password.$error,
                          }"
                        ></b-form-input>
                        <v-icon
                          class="position-absolute"
                          style="right: 5px; top: 8px"
                          @click="showConfirmPassword = !showConfirmPassword"
                          >{{
                            showConfirmPassword ? "mdi-eye" : "mdi-eye-off"
                          }}</v-icon
                        >
                        <div
                          v-if="submitted && !$v.user.confirm_password.required"
                          class="invalid-feedback"
                        >
                          field is required
                        </div>
                        <div
                          v-if="
                            submitted &&
                            !$v.user.confirm_password.sameAsPassword
                          "
                          class="invalid-feedback"
                        >
                          confirmation password doesn't match password
                        </div>
                      </div>
                    </b-form-group>

                    <v-checkbox v-model="terms" required :rules="rules.terms">
                      <template v-slot:label>
                        <div>
                          I agree to the
                          <v-tooltip top>
                            <template v-slot:activator="{ on }">
                              <a
                                target="_blank"
                                href="/terms-of-service"
                                @click.stop
                                v-on="on"
                                class="primary--text"
                              >
                                Terms of Service
                              </a>
                            </template>
                            Read Terms of Service
                          </v-tooltip>
                        </div>
                      </template>
                    </v-checkbox>
                    <v-checkbox
                      required
                      class="mt-0"
                      v-model="privacy"
                      :rules="rules.privacy"
                    >
                      <template v-slot:label>
                        <div>
                          I agree to the
                          <v-tooltip top>
                            <template v-slot:activator="{ on }">
                              <a
                                target="_blank"
                                href="/privacy-policy"
                                @click.stop
                                v-on="on"
                                class="primary--text"
                              >
                                Privacy Policy
                              </a>
                            </template>
                            Read Privacy Policy
                          </v-tooltip>
                        </div>
                      </template>
                    </v-checkbox>
                    <v-btn
                      :loading="loader"
                      type="submit"
                      block
                      color="pink_secondary"
                      class="font-weight-bold white--text mt-5"
                      large
                      >Signup</v-btn
                    >
                    <p class="mt-4">
                      Already a member?
                      <a href="/login" class="font-weight-bold pink--text">
                        Login
                      </a>
                    </p>
                  </v-form>
                </v-container>
              </v-card>
            </v-col>
          </v-row>
        </v-col>
        <v-snackbar
          color="red accent-2"
          elavation="24"
          top
          right
          v-model="isRegisterError"
        >
          <div v-html="regError"></div>
        </v-snackbar>
      </v-row>
    </v-container>
  </div>
</template>

<style lang="scss">
.tel-border {
  border: 1px solid #ced4da !important;
}
.vue-tel-input {
  height: 36px !important;
  color: #495057;
  background-color: #fff !important;
  // border: 1px solid #ced4da !important;
  border-radius: 0.25rem;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
  &:focus-within {
    box-shadow: unset !important;
  }
  ul::-webkit-scrollbar {
    width: 6px !important;
  }

  ul::-webkit-scrollbar-track {
    background-color: darkgrey !important;
  }

  ul::-webkit-scrollbar-thumb {
    box-shadow: inset 0 0 6px rgba(0, 0, 0, 0.3) !important;
  }

  ul {
    overflow-x: hidden !important;
  }
}
.vti__search_box {
  width: 99%;
}
.vti__dropdown-list {
  padding-left: 0 !important;
  width: 56vw !important;
  max-height: 130px !important;
}
.vti__dropdown {
  border-right: 1px solid #bbb;
  &:hover {
    background: transparent !important;
  }
}

@media screen and (max-width: 768px) {
  .vti__dropdown-list {
    width: 78vw !important;
    max-height: 140px !important;
  }
}
#registration {
  height: 100%;
  min-width: 100% !important;
}

.row-content {
  margin-top: 40%;
}

.form-content {
  margin-top: 20%;
}

.logo {
  position: absolute;
  width: 180px;
  left: 20px;
  top: 5px;
}

.logo img {
  width: 100%;
}

.home {
  position: absolute;
  right: 20px;
  top: 5px;
  font-size: 50px;
}

.v-footer {
  display: none !important;
}
</style>
